import type { MetroArea, Quantile, WatchlistDto, WatchlistGroup } from "@lossdog/job-watchlists";
import { cx } from "class-variance-authority";
import { useEffect, useRef, useState } from "react";

import { ArrowDropdownSmallIcon } from "@/assets/icons/general/ArrowDropdownSmall";
import { SearchIcon } from "@/assets/icons/general/Search";
import { Button } from "@/components/ui/buttons/Button/Button";
import { TextInput } from "@/components/ui/inputs/TextInput/TextInput";
import { Dropdown } from "@/components/ui/overlays/Dropdown/Dropdown";
import { Typography } from "@/components/ui/text/Typography/Typography";

export type WatchlistItem = (WatchlistGroup | WatchlistDto | MetroArea | Quantile) & { shouldHighlight?: boolean };

export const WatchlistDropdown = ({
  data,
  selectedItem,
  setSelectedItem,
  selectedItemLabel,
  hasSearch,
  onSearch,
  btnClassName,
  popperClassname,
  triggerClassName,
  useTriggerWidth,
}: {
  selectedItem: { id: string; title: string } | null;
  setSelectedItem: (item: { id: string; title: string } | null) => void;
  data: WatchlistItem[];
  hasSearch?: boolean;
  onSearch?: (search: string) => void;
  btnClassName?: string;
  selectedItemLabel?: React.ReactNode;
  popperClassname?: string;
  triggerClassName?: string;
  useTriggerWidth?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const highlightedItems = data.filter((item) => item.shouldHighlight);
  const nonHighlightedItems = data.filter((item) => !item.shouldHighlight);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, inputRef]);

  return (
    <Dropdown
      customTrigger={
        <Button
          icon={ArrowDropdownSmallIcon}
          variant="primary"
          iconPosition="right"
          className={cx(
            "!w-full !border-elevation-outline-1 !bg-elevation-surface-1 !text-text-inverted-tertiary border py-0 pr-0 pl-1-5",
            btnClassName,
          )}
          iconClassName="h-6 w-6 text-text-inverted-tertiary"
        >
          {selectedItemLabel || (
            <Typography
              size="body-3"
              className="w-full text-start"
            >
              {selectedItem?.title}
            </Typography>
          )}
        </Button>
      }
      btnClassName={btnClassName}
      triggerClassName={triggerClassName}
      isOpen={isOpen}
      setIsOpen={(value) => {
        setIsOpen(value);
        onSearch?.("");
      }}
      popoverProps={{
        className: cx("w-80 !max-h-[500px] overflow-y-auto !p-0", popperClassname),
      }}
      useTriggerWidth={useTriggerWidth}
    >
      {hasSearch && (
        <TextInput
          icon={SearchIcon}
          placeholder="Search"
          hideLabel
          label="Search"
          variant="search"
          onChange={(e) => onSearch?.(e.target.value)}
          className="mb-3 w-full"
          ref={inputRef}
        />
      )}
      {highlightedItems.map((item: WatchlistItem, index: number) => (
        <div
          key={item.id}
          className={cx(
            "flex cursor-pointer items-center justify-between px-3-5 py-2 text-interactive-text-hover hover:bg-elevation-surface-2",
            selectedItem?.id === item.id && "bg-elevation-surface-2",
            index === highlightedItems.length - 1 && "mb-1 border-elevation-outline-1 border-b",
          )}
          onClick={() => {
            setSelectedItem(item);
            onSearch?.("");
            setIsOpen(false);
          }}
        >
          <Typography
            size="body-3"
            className="text-interactive-text-on-bg"
          >
            {item.title.replace("exp", "")}
          </Typography>
        </div>
      ))}
      {nonHighlightedItems.map((item: WatchlistItem) => (
        <div
          key={item.id}
          className={cx(
            "flex cursor-pointer items-center justify-between gap-2 px-3-5 py-2 text-interactive-text-hover hover:bg-elevation-surface-2",
            selectedItem?.id === item.id && "bg-elevation-surface-2",
          )}
          onClick={() => {
            setSelectedItem(item);
            onSearch?.("");
            setIsOpen(false);
          }}
        >
          <Typography
            size="body-3"
            className="text-interactive-text-on-bg"
          >
            {item.title.replace("exp", "")}
          </Typography>
        </div>
      ))}
    </Dropdown>
  );
};
